import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Delete, DragIndicator, Edit } from "@mui/icons-material";
import { CSSProperties, MouseEvent, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import __ from "src/utils/Translations";
import DefaultIconWithPaper from "src/Pages/Company/Dashboard/pages/Widgets/shared/DefaultIconWithPaper";

interface LinkItemProps {
  id: string;
  name: string;
  icon: { original: string } | null;
  onEdit?: () => void;
  onRemove?: () => void;
  draggable?: boolean;
  showActions?: boolean;
}

const LinkItem = ({
  id,
  name,
  icon,
  onEdit,
  onRemove,
  draggable = false,
  showActions = false,
}: LinkItemProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !draggable });

  const style: CSSProperties = draggable
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : 0,
      }
    : {};

  const handleMenuOpen = (e: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Stack
      ref={draggable ? setNodeRef : undefined}
      style={style}
      spacing="0.75rem"
      sx={{
        textAlign: "center",
        alignItems: "center",
        margin: "0 0 0 0.25rem",
      }}
      data-testid="link-item"
    >
      <Stack direction="row" spacing="0.25rem" sx={{ alignItems: "center" }}>
        {draggable && (
          <IconButton
            {...listeners}
            {...attributes}
            size="small"
            aria-label={__("Przeciągnij, aby zmienić kolejność")}
            data-testid="link-item__drag-handle"
            sx={{
              cursor: isDragging ? "grabbing !important" : "grab !important",
              padding: 0,
            }}
          >
            <DragIndicator
              sx={{ pointer: "grab", padding: 0 }}
              fontSize="small"
            />
          </IconButton>
        )}
        <Box sx={{ position: "relative" }}>
          {icon ? (
            <img
              src={icon.original}
              alt={name}
              data-testid="link-item__icon"
              style={{
                width: "72px",
                height: "72px",
                borderRadius: "1rem",
                margin: "0 0 0.25rem 0",
              }}
            />
          ) : (
            <DefaultIconWithPaper />
          )}
          {showActions && (
            <>
              <Button
                size="small"
                onClick={handleMenuOpen}
                variant="contained"
                data-testid="link-item__edit-button"
                sx={{
                  display: "flex",
                  position: "absolute",
                  bottom: "-0.5rem",
                  right: "-0.5rem",
                  minWidth: 0,
                  width: "2.25rem",
                  height: "2.25rem",
                  padding: 0,
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M12.546 3.77813C12.6497 3.77813 12.7219 3.77822 12.7693 3.77835L12.8243 3.77861L12.8423 3.7788L12.8519 3.77899L12.8591 3.7792L12.8645 3.77941L12.8726 3.77982C12.8757 3.77999 12.8807 3.78029 12.8869 3.78079L12.8994 3.78193C12.9039 3.7824 12.9143 3.78364 12.9202 3.78443C12.9287 3.78566 12.9496 3.78926 12.9622 3.79179C12.9832 3.79668 13.0445 3.81583 13.0838 3.83225C13.18 3.88873 13.3905 4.20727 13.3774 4.48088C13.2907 4.64405 13.0964 4.80154 13.0371 4.82667C13.0096 4.83578 12.966 4.84718 12.9502 4.85042C12.9402 4.85226 12.9232 4.85495 12.9162 4.8559C12.9111 4.85655 12.9022 4.85757 12.8983 4.85798L12.8872 4.85898C12.8816 4.85943 12.877 4.85973 12.8741 4.8599L12.8665 4.8603L12.8575 4.86065L12.8519 4.86082L12.8435 4.861L12.828 4.86122L12.8099 4.86141C12.7838 4.86164 12.7475 4.86186 12.7084 4.86203C12.6692 4.8622 12.6265 4.86233 12.5877 4.86238L12.5329 4.8624L12.4881 4.86228L12.47 4.86214L12.4615 4.86203L12.4521 4.86185L12.4461 4.86169L12.4378 4.86138L12.4318 4.86109L12.4232 4.86059L12.4152 4.86H6.44793C6.19654 4.86 5.95546 4.95603 5.7777 5.12697C5.59993 5.29791 5.50006 5.52975 5.50006 5.77148V11.2923C5.50006 11.3023 5.49978 11.3122 5.49923 11.3221C5.49978 11.3319 5.50006 11.3418 5.50006 11.3518V14.2566C5.50006 14.4984 5.59993 14.7302 5.7777 14.9011C5.95547 15.0721 6.19656 15.1681 6.44793 15.1681H12.5447C12.796 15.1681 13.0371 15.0721 13.2149 14.9011C13.3927 14.7302 13.4925 14.4984 13.4925 14.2566V12.8718C13.4925 12.5731 13.7444 12.3309 14.0551 12.3309C14.3657 12.3309 14.6176 12.5731 14.6176 12.8718V14.2566C14.6176 14.7853 14.3992 15.2923 14.0105 15.6661C13.6217 16.04 13.0944 16.25 12.5447 16.25H6.44793C5.89814 16.25 5.3709 16.04 4.98216 15.6661C4.59342 15.2923 4.375 14.7853 4.375 14.2566V11.3518C4.375 11.3418 4.37528 11.3319 4.37584 11.3221C4.37528 11.3122 4.375 11.3023 4.375 11.2923V5.77148C4.375 5.2428 4.59341 4.73578 4.98216 4.36196C5.37091 3.98814 5.89816 3.77813 6.44793 3.77813H12.546Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.6476 3.90844C15.428 3.69719 15.0718 3.69719 14.8521 3.90844L8.67304 9.85029L8.53972 10.0404L7.41495 12.7443C7.36288 12.8689 7.35869 13.0123 7.41465 13.1468C7.53004 13.4242 7.85741 13.5591 8.14587 13.4482L10.947 12.3708C10.986 12.3567 11.0237 12.3384 11.0594 12.3157C11.095 12.2932 11.1274 12.2671 11.1562 12.2381L17.3352 6.29625C17.5549 6.085 17.5549 5.7425 17.3352 5.53125L15.6476 3.90844ZM9.74861 11.6665L8.94673 11.9749L9.26748 11.2038L9.74861 11.6665ZM9.85756 10.2412L10.7496 11.0991L16.1419 5.91375L15.2499 5.05594L9.85756 10.2412Z"
                    fill="white"
                  />
                </svg>
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                data-testid="link-item__menu"
              >
                <MenuItem
                  onClick={() => {
                    handleClose();
                    onEdit?.();
                  }}
                  data-testid="link-item__menu-edit"
                >
                  <ListItemIcon>
                    <Edit fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{__("Edytuj")}</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleClose();
                    onRemove?.();
                  }}
                  data-testid="link-item__menu-remove"
                >
                  <ListItemIcon>
                    <Delete fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{__("Usuń")}</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Stack>
      <Tooltip title={name} arrow>
        <Typography
          variant="caption"
          data-testid="link-item__name"
          sx={{
            margin: "1rem 0 0 auto !important",
            textAlign: "center",
            maxWidth: "72px",
            width: "72px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            overflowWrap: "break-word",
          }}
        >
          {name}
        </Typography>
      </Tooltip>
    </Stack>
  );
};

export default LinkItem;
